import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { mapOptimizerPortfolio } from '../../../hooks/useOptimizer';
import { requireOptimizerPortfolioList } from '../../optimizer/optimizerPortfolioMutationContract';

// Recovery puts a saved portfolio's securities back in the Watchlist through the
// ordinary review/apply boundary, then refreshes its compatibility. No
// continuation may mistake a staged revision, partial publication, or stale
// user session for approval.
export default function useOptimizerWatchlistRecovery({
    userUid, sessionScopeToken, publicationState, fetchOptimizerPortfolios,
    onNotice,
}) {
    const [pending, setPending] = useState(null);
    const pendingRef = useRef(null);
    const startedRef = useRef(null);
    const latest = useRef(null);
    const mountedRef = useRef(false);
    useLayoutEffect(() => {
        latest.current = { userUid, sessionScopeToken, publicationState,
            fetchOptimizerPortfolios, onNotice };
    });
    useLayoutEffect(() => {
        mountedRef.current = true;
        pendingRef.current = null;
        startedRef.current = null;
        setPending(null);
        return () => { mountedRef.current = false; pendingRef.current = null; };
    }, [userUid, sessionScopeToken]);

    const isCurrent = (request) => mountedRef.current && pendingRef.current === request
        && latest.current.userUid === request.userUid
        && latest.current.sessionScopeToken === request.sessionScopeToken;
    const clear = (request) => {
        if (!isCurrent(request)) return;
        pendingRef.current = null;
        setPending(null);
    };
    const checkPortfolio = async (request) => {
        const response = await latest.current.fetchOptimizerPortfolios({
            force: true, isCurrentRequest: () => isCurrent(request),
        });
        if (!isCurrent(request)) return null;
        const raw = requireOptimizerPortfolioList(response).find((item) => String(item.id) === String(request.portfolio.id));
        if (!raw) throw new Error('The saved portfolio is no longer available.');
        const portfolio = mapOptimizerPortfolio(raw);
        if (portfolio.portfolioVersion !== request.portfolio.portfolioVersion
            || portfolio.receiptId !== request.portfolio.receiptId) {
            throw new Error('The saved portfolio changed during restoration. Review it before continuing.');
        }
        if (portfolio.compatibility?.current_watchlist_revision_id !== request.revisionId
            || (request.requiresPublication && (latest.current.publicationState?.revisionId !== request.revisionId
                || latest.current.publicationState?.committed !== true))) {
            throw new Error('The Watchlist changed again during restoration. Review the saved portfolio before continuing.');
        }
        if (portfolio.compatibility?.status !== 'compatible' || portfolio.compatibility?.missing_security_count !== 0) {
            throw new Error('Some saved securities could not be restored. Review Security Resolution before continuing.');
        }
        return portfolio;
    };
    const complete = (request) => {
        if (!isCurrent(request)) return;
        latest.current.onNotice(
            'Watchlist recovered. Use Re-optimize in the saved portfolio’s details when you are ready.', 'success',
        );
        clear(request);
    };
    const continueAfterPublication = useCallback((response, recovery) => {
        if (!mountedRef.current || latest.current.userUid !== userUid
            || latest.current.sessionScopeToken !== sessionScopeToken) return false;
        const request = { ...recovery, revisionId: response.watchlist_revision_id,
            requiresPublication: response.status !== 'not_needed', userUid, sessionScopeToken };
        pendingRef.current = request;
        startedRef.current = null;
        setPending(request);
        return true;
    }, [userUid, sessionScopeToken]);

    useEffect(() => {
        if (!pending || pendingRef.current !== pending || startedRef.current === pending) return;
        if (pending.requiresPublication) {
            if (publicationState?.revisionId !== pending.revisionId) return;
            if (['failed', 'cancelled', 'superseded', 'unchanged'].includes(publicationState.lifecycle)) {
                onNotice('Watchlist restoration did not complete. Holdings and the saved optimization were not changed.', 'warning');
                clear(pending);
                return;
            }
            if (publicationState.committed !== true) return;
        }
        startedRef.current = pending;
        (async () => {
            try {
                const portfolio = await checkPortfolio(pending);
                if (!portfolio || !isCurrent(pending)) return;
                complete(pending);
            } catch (error) {
                if (isCurrent(pending)) { latest.current.onNotice(error.message, 'error'); clear(pending); }
            }
        })();
    }, [pending, publicationState, onNotice]);

    return { continueAfterPublication };
}
